import { Button, Select, type SelectOption } from '@/shared/ui';
import { useForm } from '@tanstack/react-form';
import { type FC } from 'react';

import { FacilityFloorPlanFiltersFormValuesSchema } from '../model/contracts';
import { useFacilityFloorPlanFiltersStore } from '../model/store';
import type { FacilityFloorPlanFiltersFormValues } from '../model/types';

const STATUS_OPTIONS: SelectOption[] = [
  { label: 'Vacant', value: 'VACANT' },
  { label: 'Occupied', value: 'OCCUPIED' },
];

type FacilityFloorPlanFiltersFormProps = {
  isLoading?: boolean;
  /**
   * Portfolio Mode only. Omit to hide the Facility filter — in Single Mode
   * every bed belongs to the same facility, so it would filter nothing.
   */
  facilitiesOptions?: SelectOption[];
};

export const FacilityFloorPlanFiltersForm: FC<
  FacilityFloorPlanFiltersFormProps
> = ({ isLoading, facilitiesOptions }) => {
  const setFilters = useFacilityFloorPlanFiltersStore(
    (state) => state.setFilters
  );
  const { statuses, facilities } = useFacilityFloorPlanFiltersStore(
    (state) => state
  );

  const showFacilityFilter = Boolean(facilitiesOptions?.length);

  const form = useForm({
    defaultValues: {
      statuses: statuses?.length ? statuses : [],
      facilities,
    } as FacilityFloorPlanFiltersFormValues,

    validators: { onChange: FacilityFloorPlanFiltersFormValuesSchema },
    onSubmit: ({ value }) => {
      const statusesValues = value.statuses?.length ? value.statuses : null;
      const facilitiesValues = value.facilities?.length
        ? value.facilities
        : null;

      setFilters({
        statuses: statusesValues,
        floors: null,
        facilities: facilitiesValues,
        currentPage: 1,
      });
    },
  });

  return (
    <form
      className="p-3"
      onSubmit={(event) => {
        event.preventDefault();
        form.handleSubmit();
      }}
    >
      <div className="grid grid-cols-3 gap-6">
        {showFacilityFilter && (
          <form.Field
            name="facilities"
            children={({ state, setValue, handleBlur }) => (
              <div className="flex w-full flex-col gap-1">
                <Select
                  isMulti
                  withTags
                  selectAll
                  clearedValue
                  label="Facility"
                  className="min-w-[200px]"
                  placeholder="All facilities"
                  value={state.value}
                  options={facilitiesOptions ?? []}
                  onBlur={handleBlur}
                  onChange={(val) => setValue(val as SelectOption[])}
                />
              </div>
            )}
          />
        )}

        <form.Field
          name="statuses"
          children={({ state, setValue, handleBlur }) => (
            <div className="flex w-full flex-col gap-1">
              <Select
                isMulti
                withTags
                clearedValue
                label="Status"
                className="min-w-[200px]"
                placeholder="Choose filter"
                value={state.value}
                options={STATUS_OPTIONS}
                onBlur={handleBlur}
                onChange={(val) => {
                  const selected = val as SelectOption[];
                  setValue(selected);
                }}
              />
            </div>
          )}
        />
      </div>

      {/* mt-6 matches the Alerts and Staff filter forms -- without it the
          buttons sit flush against the filter row. */}
      <div className="mt-6 flex justify-end gap-3">
        <Button
          dangerous
          variant="secondary"
          disabled={isLoading}
          onClick={() => {
            useFacilityFloorPlanFiltersStore.setState(
              useFacilityFloorPlanFiltersStore.getInitialState()
            );
            form.reset();
          }}
        >
          Clear All Filters
        </Button>

        <form.Subscribe
          selector={(state) => state.isValid && !isLoading}
          children={(isValid) => (
            <Button type="submit" variant="primary" disabled={!isValid}>
              Apply Filters
            </Button>
          )}
        />
      </div>
    </form>
  );
};
